/**
 * SB6-II-01 — Inspection Instructions list page (`/inspection-instructions`).
 *
 * Net-new Project Management surface, cloned from the Projects page
 * (SB2-W-16) structure, plus inline-editable cells (Assigned Users /
 * Company / Planned Date), Edit + Add modals, and bulk assignment.
 * Uses the canonical `DataPageShell` template.
 *
 * Filter order (locked top-to-bottom):
 *   1. Search (placeholder "Search")
 *   2. Projects multiselect — EAGER (eagerOptions)
 *   3. Sites multiselect — ASYNC typeahead
 *   4. Asset multiselect — ASYNC typeahead
 *   5. States multiselect (9-value enum)
 *   6. Show completed toggle (default OFF → showCompleted=false hides
 *      COMPLETED/WITHDRAWN/REJECTED)
 *
 * Column order locked in `columns.tsx`. Footer: total LEFT + pagination
 * RIGHT (entityLabel "inspection instructions").
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import type { GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid";
import {
  AsyncTypeaheadChipFilter,
  DataPageFooter,
  DataPageShell,
  DataPageToolbar,
  DataTable,
  SearchField,
  SingleSelectFilter,
  TypeaheadChipFilter,
  sidebar as sidebarTokens,
  typography,
  useCanEditResourceFor,
} from "@aeriosiq/ui";
import { useSearchParams } from "react-router-dom";
import { ToastProvider } from "../account/ToastContext.js";
import { useOwnerMemberIIs, type IiOrigin } from "./useOwnerMemberIIs.js";
import { useInspectionInstructionFacets } from "./useInspectionInstructionFacets.js";
import {
  II_STATE_OPTIONS,
  iiStateLabel,
} from "./InspectionInstructionStatusChip.js";
import { buildInspectionInstructionColumns } from "./columns.js";
import { EditInspectionInstructionDialog } from "./EditInspectionInstructionDialog.js";
import { AddInspectionInstructionDialog } from "./AddInspectionInstructionDialog.js";
import { BulkAssignDialog } from "./BulkAssignDialog.js";
import {
  INSPECTION_INSTRUCTION_STATES,
  II_SORT_KEYS,
  type IiListItem,
  type IiSortDir,
  type IiSortKey,
  type InspectionInstructionState,
  type ListInspectionInstructionsParams,
} from "./types.js";
import { ClipboardListIcon, PlusIcon, UserPlusIcon } from "@aeriosiq/ui/icons";

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_SORT_BY: IiSortKey = "instructionCode";
const DEFAULT_SORT_DIR: IiSortDir = "desc";

function parseSort(
  value: string | null,
): { key: IiSortKey; dir: IiSortDir } | null {
  if (!value) return null;
  const [k, d] = value.split(":");
  if (!k || !d) return null;
  if (!(II_SORT_KEYS as readonly string[]).includes(k)) return null;
  if (d !== "asc" && d !== "desc") return null;
  return { key: k as IiSortKey, dir: d };
}

function parsePageSize(value: string | null): number {
  const n = Number(value ?? "");
  if (
    !Number.isFinite(n) ||
    !PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])
  ) {
    return DEFAULT_PAGE_SIZE;
  }
  return n;
}

function parseCsv(value: string | null): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  for (const raw of value.split(",")) {
    const v = raw.trim();
    if (v.length > 0) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

export function InspectionInstructionsPage(): ReactElement {
  return (
    <ToastProvider>
      <InspectionInstructionsPageInner />
    </ToastProvider>
  );
}

function InspectionInstructionsPageInner(): ReactElement {
  const [params, setParams] = useSearchParams();
  const showCompleted = params.get("showCompleted") === "1";
  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const debouncedSearch = useDebounced(searchInput, 500);

  // SB6-II-01 — Add button gate. The role matrix has no
  // `inspection_instruction.create` permission key; per the story we
  // reuse `project.create` (same TA / PM / platform_admin role set,
  // conditional-for-tenant cell — exactly the ContractsPage pattern).
  // `useCanEditResourceFor` resolves the conditional with the same-tenant
  // default so matrix-permitted roles see the button.
  const canCreate = useCanEditResourceFor("project.create");

  const [editId, setEditId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);

  const facets = useInspectionInstructionFacets();

  const lastAppliedSearchRef = useRef(params.get("search") ?? "");
  useEffect(() => {
    if (lastAppliedSearchRef.current === debouncedSearch) return;
    lastAppliedSearchRef.current = debouncedSearch;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch.length > 0) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  const sortParsed = parseSort(params.get("sort"));
  const pageSize = parsePageSize(params.get("pageSize"));
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  // R0627-06 — record-origin lens: All (default) / Owned / Shared.
  const origin = useMemo<IiOrigin>(() => {
    const raw = params.get("origin");
    return raw === "owned" || raw === "shared" ? raw : "all";
  }, [params]);
  // FCII — link-status filter: All (default) / Linked / Unlinked. Server-side
  // via the projects/v1@2.23.0 `linkage` param (project_id IS NULL derivation).
  const linkage = useMemo<"all" | "linked" | "unlinked">(() => {
    const raw = params.get("linkage");
    return raw === "linked" || raw === "unlinked" ? raw : "all";
  }, [params]);
  const projectSelected = useMemo(
    () => parseCsv(params.get("project")),
    [params],
  );
  const siteSelected = useMemo(() => parseCsv(params.get("site")), [params]);
  const assetSelected = useMemo(() => parseCsv(params.get("asset")), [params]);
  const stateSelected = useMemo<InspectionInstructionState[]>(() => {
    const raw = parseCsv(params.get("state"));
    return raw.filter((s): s is InspectionInstructionState =>
      (INSPECTION_INSTRUCTION_STATES as readonly string[]).includes(s),
    );
  }, [params]);

  const queryParams = useMemo<ListInspectionInstructionsParams>(() => {
    const out: ListInspectionInstructionsParams = { page, pageSize };
    if (debouncedSearch.length > 0) out.q = debouncedSearch;
    out.sortBy = sortParsed?.key ?? DEFAULT_SORT_BY;
    out.sortDir = sortParsed?.dir ?? DEFAULT_SORT_DIR;
    out.showCompleted = showCompleted;
    if (projectSelected.length > 0) out.projectIds = projectSelected;
    if (siteSelected.length > 0) out.siteIds = siteSelected;
    if (assetSelected.length > 0) out.assetIds = assetSelected;
    if (stateSelected.length > 0) out.states = stateSelected;
    if (linkage !== "all") out.linkage = linkage;
    return out;
  }, [
    page,
    pageSize,
    debouncedSearch,
    sortParsed,
    showCompleted,
    linkage,
    projectSelected,
    siteSelected,
    assetSelected,
    stateSelected,
  ]);

  // TNCY-9 §c — fire both scopes + merge client-side; member rows carry the
  // `<OwnershipBadge>` (distinct from the Assigned Company column). Own is the
  // spine (its pending/error drives the list). The BE deduped the member set
  // server-side; `mergeOwnerMember` dedupes again defensively.
  const {
    rows: mergedRows,
    total,
    isPending,
    isError,
  } = useOwnerMemberIIs(queryParams, origin);

  const projectOptions = useMemo(
    () =>
      (facets.projects.data?.items ?? []).map((o) => ({
        id: o.id,
        label: o.label,
      })),
    [facets.projects.data?.items],
  );
  const siteOptions = useMemo(
    () => facets.sites.data?.items ?? [],
    [facets.sites.data?.items],
  );
  const assetOptions = useMemo(
    () => facets.assets.data?.items ?? [],
    [facets.assets.data?.items],
  );

  // Label caches so async-filter chips render a human label even when
  // the search input is cleared (the option leaves `options`).
  const siteHints = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of siteOptions) m.set(o.id, o.label);
    return m;
  }, [siteOptions]);
  const assetHints = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of assetOptions) m.set(o.id, o.label);
    return m;
  }, [assetOptions]);

  function setCsvParam(key: string, values: readonly string[]): void {
    const canonical = [...new Set(values.map((c) => c.trim()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) next.delete(key);
        else next.set(key, canonical.join(","));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setStateSelected(values: readonly string[]): void {
    const canonical = [...new Set(values)]
      .filter((v): v is InspectionInstructionState =>
        (INSPECTION_INSTRUCTION_STATES as readonly string[]).includes(v),
      )
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) next.delete("state");
        else next.set("state", canonical.join(","));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setOrigin(next: string): void {
    setParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next === "owned" || next === "shared") {
          nextParams.set("origin", next);
        } else {
          nextParams.delete("origin");
        }
        nextParams.delete("page");
        return nextParams;
      },
      { replace: true },
    );
  }
  function setLinkage(next: string): void {
    setParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next === "linked" || next === "unlinked") {
          nextParams.set("linkage", next);
        } else {
          nextParams.delete("linkage");
        }
        nextParams.delete("page");
        return nextParams;
      },
      { replace: true },
    );
  }
  function toggleShowCompleted(checked: boolean): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) next.set("showCompleted", "1");
        else next.delete("showCompleted");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setPage(p: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p <= 1) next.delete("page");
        else next.set("page", String(p));
        return next;
      },
      { replace: true },
    );
  }
  function setPageSize(n: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (n === DEFAULT_PAGE_SIZE) next.delete("pageSize");
        else next.set("pageSize", String(n));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  const sortModel = useMemo<GridSortModel>(() => {
    const key = sortParsed?.key ?? DEFAULT_SORT_BY;
    const dir = sortParsed?.dir ?? DEFAULT_SORT_DIR;
    return [{ field: key, sort: dir }];
  }, [sortParsed]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const head = model[0];
      if (!head?.sort) {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("sort");
            return next;
          },
          { replace: true },
        );
        return;
      }
      if (!(II_SORT_KEYS as readonly string[]).includes(head.field)) {
        return;
      }
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sort", `${head.field}:${head.sort ?? "asc"}`);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const onEdit = useCallback((row: IiListItem) => {
    setEditId(row.inspectionInstructionId);
  }, []);

  const columns = useMemo(
    () =>
      buildInspectionInstructionColumns({
        onEdit,
        // R0627-05 — origin column always on (house icon for own rows too),
        // so the provenance tell is consistent across the PM pages.
        showOwnership: true,
      }),
    [onEdit],
  );

  const rows = mergedRows;
  const selectedIds = selection.map((id) => String(id));

  return (
    <DataPageShell title="Inspection Instructions">
      <DataPageToolbar
        comfortableFilters
        ariaLabel="Inspection instructions toolbar"
        filtersTestId="inspection-instructions-toolbar-filters"
        settingsTestId="inspection-instructions-toolbar-settings"
        filters={
          <>
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search"
              ariaLabel="Search inspection instructions"
            />
            {/* Projects — eager (all options on mount). */}
            <TypeaheadChipFilter
              label="Projects"
              eagerOptions
              value={projectSelected}
              onChange={(next) => {
                setCsvParam("project", next);
              }}
              options={projectOptions}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.label}
            />
            {/* Sites — async typeahead. */}
            <AsyncTypeaheadChipFilter
              label="Sites"
              value={siteSelected}
              onChange={(next) => {
                setCsvParam("site", next);
              }}
              options={siteOptions}
              loading={facets.sites.isFetching}
              selectedHints={siteHints}
              onInputChange={facets.setSiteQuery}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.label}
            />
            {/* Asset — async typeahead. */}
            <AsyncTypeaheadChipFilter
              label="Asset"
              value={assetSelected}
              onChange={(next) => {
                setCsvParam("asset", next);
              }}
              options={assetOptions}
              loading={facets.assets.isFetching}
              selectedHints={assetHints}
              onInputChange={facets.setAssetQuery}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.label}
            />
            {/* States — eager enum multiselect. */}
            <TypeaheadChipFilter
              label="States"
              eagerOptions
              value={stateSelected}
              onChange={setStateSelected}
              options={II_STATE_OPTIONS}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => iiStateLabel(o.id)}
            />
            {/* R0627-06 — record-origin filter (All / Owned / Shared). */}
            <SingleSelectFilter
              label="Origin"
              value={origin}
              options={[
                { value: "all", label: "All", display: "" },
                { value: "owned", label: "Owned" },
                { value: "shared", label: "Shared" },
              ]}
              onChange={setOrigin}
            />
            {/* FCII — link-status filter (All / Linked / Unlinked). */}
            <SingleSelectFilter
              label="Link status"
              value={linkage}
              options={[
                { value: "all", label: "All", display: "" },
                { value: "linked", label: "Linked" },
                { value: "unlinked", label: "Unlinked" },
              ]}
              onChange={setLinkage}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showCompleted}
                  onChange={(_e, c) => {
                    toggleShowCompleted(c);
                  }}
                  inputProps={{ "aria-label": "Show completed" }}
                />
              }
              label="Show completed"
              sx={{ ml: 0, mr: 0 }}
            />
          </>
        }
        settings={
          <>
            {selectedIds.length > 0 ? (
              <Button
                aria-label="Assign users"
                onClick={() => {
                  setBulkOpen(true);
                }}
                startIcon={<UserPlusIcon fontSize="small" />}
                size="small"
                sx={{ textTransform: "none" }}
              >
                Assign users ({String(selectedIds.length)})
              </Button>
            ) : null}
            <Box sx={{ flex: 1 }} />
            {canCreate ? (
              <Button
                aria-label="Add Inspection Instruction"
                onClick={() => {
                  setAddOpen(true);
                }}
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                sx={(t) => {
                  const mode = t.palette.mode;
                  const tokens =
                    mode === "dark" ? sidebarTokens.dark : sidebarTokens.light;
                  return {
                    height: 32,
                    minHeight: 32,
                    paddingInline: 10,
                    borderRadius: 1.5,
                    fontSize: typography.size["body-md"],
                    fontWeight: typography.weight.medium,
                    letterSpacing: typography.tracking["body-md"],
                    lineHeight: 1,
                    bgcolor: tokens.selectedBg,
                    color: tokens.selectedFg,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: t.palette.primary.light,
                      boxShadow: "none",
                    },
                    "&:active": {
                      bgcolor: tokens.rowPressedBg,
                      color: tokens.selectedFg,
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${t.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: 1,
                      marginLeft: -0.25,
                    },
                  };
                }}
              >
                Add Inspection Instruction
              </Button>
            ) : null}
          </>
        }
      />

      {isPending ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Couldn&apos;t load inspection instructions.
        </Typography>
      ) : rows.length === 0 ? (
        <InspectionInstructionsEmptyState />
      ) : (
        <DataTable<IiListItem & { id: string }>
          rows={rows.map((r) => ({ ...r, id: r.inspectionInstructionId }))}
          columns={columns as never}
          ariaLabel="Inspection instructions"
          getRowId={(row) => row.id}
          rowCount={total}
          checkboxSelection
          keepNonExistentRowsSelected
          rowSelectionModel={selection}
          onRowSelectionModelChange={(model) => {
            setSelection(model);
          }}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => {
            const nextPage = m.page + 1;
            if (m.pageSize !== pageSize) {
              setPageSize(m.pageSize);
            } else if (nextPage !== page) {
              setPage(nextPage);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pinnedActionColumn
        />
      )}

      <DataPageFooter
        total={total}
        entityLabel="inspection instructions"
        entityLabelSingular="inspection instruction"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        totalTestId="inspection-instructions-footer-total"
      />

      <EditInspectionInstructionDialog
        inspectionInstructionId={editId}
        onClose={() => {
          setEditId(null);
        }}
      />
      <AddInspectionInstructionDialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
      />
      <BulkAssignDialog
        open={bulkOpen}
        onClose={() => {
          setBulkOpen(false);
        }}
        instructionIds={selectedIds}
      />
    </DataPageShell>
  );
}

function InspectionInstructionsEmptyState(): ReactElement {
  return (
    <Box
      sx={{
        flex: 1,
        py: 8,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <ClipboardListIcon
        sx={{ fontSize: 48, color: "text.secondary" }}
        aria-hidden
      />
      <Typography variant="h6" color="text.primary">
        No inspection instructions yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Inspection instructions appear here once they are created for your
        tenant.
      </Typography>
    </Box>
  );
}
